import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import {
  buildEvolutionSavePayload,
  extractEvolutionIdFromResponse,
  hasMeaningfulEvolutionDraftContent,
  type BuildEvolutionSavePayloadInput,
} from './buildEvolutionSavePayload';
import type { EvolutionOutcome } from './evolutionOutcomeModel';
import type { EvolutionPlanExecutionRecord } from './evolutionPlanExecution';
import type {
  EvolutionAttachedPrintable,
  EvolutionIndicationItem,
} from './evolutionWorkflowTypes';

export type EvolutionDraftSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DELAY_MS = 2500;

export interface UseEvolutionDraftAutosaveArgs {
  enabled: boolean;
  evolutionId: string | null;
  onEvolutionId: (id: string) => void;
  form: Record<string, any>;
  procedureLabels: string[];
  indicationItems: EvolutionIndicationItem[];
  planExecutionRecords: EvolutionPlanExecutionRecord[];
  indicationSelections: Record<string, { od: boolean; oe: boolean; ao: boolean }> | null;
  attachedPrintables: EvolutionAttachedPrintable[];
  indicationPrintable: EvolutionAttachedPrintable | null;
  outcome: EvolutionOutcome;
  /** Evolução já concluída editada no prazo: grava sem voltar a rascunho. */
  preserveStatus?: boolean;
  userId?: string;
}

type DraftSnapshotOverrides = Partial<
  Pick<
    BuildEvolutionSavePayloadInput,
    | 'form'
    | 'procedureLabels'
    | 'indicationItems'
    | 'planExecutionRecords'
    | 'indicationSelections'
    | 'attachedPrintables'
    | 'indicationPrintable'
    | 'outcome'
  >
>;

export type FlushAutosaveOptions = {
  /** Ignora `enabled=false` (ex.: botão Concluir) e aguarda gravação em andamento. */
  force?: boolean;
  /** Só aguarda POST/PUT pendente e devolve o id — usado antes do PUT final de Concluir. */
  waitOnly?: boolean;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useEvolutionDraftAutosave({
  enabled,
  evolutionId,
  onEvolutionId,
  form,
  procedureLabels,
  indicationItems,
  planExecutionRecords,
  indicationSelections,
  attachedPrintables,
  indicationPrintable,
  outcome,
  preserveStatus = false,
  userId,
}: UseEvolutionDraftAutosaveArgs) {
  const [status, setStatus] = useState<EvolutionDraftSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const evolutionIdRef = useRef(evolutionId);
  const savingRef = useRef(false);
  const skipWorkflowAutosaveRef = useRef(true);
  const lastSavedJsonRef = useRef<string | null>(null);
  const lastSaveFailedRef = useRef(false);

  const formRef = useRef(form);
  const procedureLabelsRef = useRef(procedureLabels);
  const indicationItemsRef = useRef(indicationItems);
  const planExecutionRecordsRef = useRef(planExecutionRecords);
  const indicationSelectionsRef = useRef(indicationSelections);
  const attachedPrintablesRef = useRef(attachedPrintables);
  const indicationPrintableRef = useRef(indicationPrintable);
  const outcomeRef = useRef(outcome);

  formRef.current = form;
  procedureLabelsRef.current = procedureLabels;
  indicationItemsRef.current = indicationItems;
  planExecutionRecordsRef.current = planExecutionRecords;
  indicationSelectionsRef.current = indicationSelections;
  attachedPrintablesRef.current = attachedPrintables;
  indicationPrintableRef.current = indicationPrintable;
  outcomeRef.current = outcome;
  evolutionIdRef.current = evolutionId;

  useEffect(() => {
    evolutionIdRef.current = evolutionId;
  }, [evolutionId]);

  useEffect(() => {
    if (enabled) {
      skipWorkflowAutosaveRef.current = true;
    }
  }, [enabled, evolutionId]);

  const buildDraftPayload = useCallback(
    (overrides?: DraftSnapshotOverrides) => {
      return buildEvolutionSavePayload({
        form: overrides?.form ?? formRef.current,
        procedureLabels: overrides?.procedureLabels ?? procedureLabelsRef.current,
        indicationItems: overrides?.indicationItems ?? indicationItemsRef.current,
        planExecutionRecords: overrides?.planExecutionRecords ?? planExecutionRecordsRef.current,
        indicationSelections: overrides?.indicationSelections ?? indicationSelectionsRef.current,
        attachedPrintables: overrides?.attachedPrintables ?? attachedPrintablesRef.current,
        indicationPrintable: overrides?.indicationPrintable ?? indicationPrintableRef.current,
        outcome: overrides?.outcome ?? outcomeRef.current,
        isCreate: !evolutionIdRef.current,
        attachCrm: false,
        preserveStatus,
        userId,
      });
    },
    [preserveStatus, userId],
  );

  // Linha de base ao abrir uma evolução existente: reabrir sem editar não gera PUT.
  useEffect(() => {
    if (!enabled) {
      lastSavedJsonRef.current = null;
      lastSaveFailedRef.current = false;
      return;
    }
    if (evolutionIdRef.current && lastSavedJsonRef.current === null) {
      lastSavedJsonRef.current = JSON.stringify(buildDraftPayload());
    }
  }, [enabled]);

  const saveDraft = useCallback(
    async (
      overrides?: DraftSnapshotOverrides,
      options?: FlushAutosaveOptions,
    ): Promise<string | null> => {
      const force = options?.force === true;

      while (savingRef.current) {
        await sleep(50);
      }

      if (!enabled && !force) {
        return evolutionIdRef.current;
      }

      const payload = buildDraftPayload(overrides);
      if (!payload.patient_id || !payload.professional_id) return evolutionIdRef.current;

      const isCreate = !evolutionIdRef.current;
      if (isCreate && !force && !hasMeaningfulEvolutionDraftContent(payload)) {
        return null;
      }

      const payloadJson = JSON.stringify(payload);
      if (!isCreate && payloadJson === lastSavedJsonRef.current) {
        setStatus((prev) => (prev === 'pending' ? 'idle' : prev));
        return evolutionIdRef.current;
      }

      savingRef.current = true;
      setStatus('saving');
      try {
        if (evolutionIdRef.current) {
          await axios.put(getApiUrl(`/clinic/evolutions/${evolutionIdRef.current}`), payload);
        } else {
          const res = await axios.post(getApiUrl('/clinic/evolutions'), payload);
          const id = extractEvolutionIdFromResponse(res);
          if (id) {
            evolutionIdRef.current = id;
            onEvolutionId(id);
          }
        }
        lastSavedJsonRef.current = isCreate ? JSON.stringify(buildDraftPayload(overrides)) : payloadJson;
        lastSaveFailedRef.current = false;
        setLastError(null);
        setStatus('saved');
        setLastSavedAt(new Date());
        return evolutionIdRef.current;
      } catch (err: any) {
        lastSaveFailedRef.current = true;
        setLastError(
          err?.response?.data?.error || err?.response?.data?.message || err?.message || null,
        );
        setStatus('error');
        return evolutionIdRef.current;
      } finally {
        savingRef.current = false;
      }
    },
    [enabled, buildDraftPayload, onEvolutionId],
  );

  const scheduleFormAutosave = useCallback(() => {
    if (!enabled) return;
    if (!formRef.current.patient_id || !formRef.current.professional_id) return;
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void saveDraft();
    }, AUTOSAVE_DELAY_MS);
  }, [enabled, saveDraft]);

  const flushAutosave = useCallback(async (options?: FlushAutosaveOptions) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    while (savingRef.current) {
      await sleep(50);
    }
    if (options?.waitOnly) {
      return evolutionIdRef.current;
    }
    return saveDraft(undefined, options);
  }, [saveDraft]);

  const saveDraftWithOverrides = useCallback(
    async (overrides: DraftSnapshotOverrides, options?: FlushAutosaveOptions) => {
      if (overrides.form) formRef.current = overrides.form;
      if (overrides.procedureLabels) procedureLabelsRef.current = overrides.procedureLabels;
      if (overrides.indicationItems) indicationItemsRef.current = overrides.indicationItems;
      if (overrides.planExecutionRecords) planExecutionRecordsRef.current = overrides.planExecutionRecords;
      if (overrides.indicationSelections !== undefined) {
        indicationSelectionsRef.current = overrides.indicationSelections;
      }
      if (overrides.attachedPrintables) attachedPrintablesRef.current = overrides.attachedPrintables;
      if (overrides.indicationPrintable !== undefined) {
        indicationPrintableRef.current = overrides.indicationPrintable;
      }
      if (overrides.outcome) outcomeRef.current = overrides.outcome;
      return saveDraft(overrides, options);
    },
    [saveDraft],
  );

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setStatus('idle');
      return;
    }
    scheduleFormAutosave();
  }, [enabled, form, scheduleFormAutosave]);

  useEffect(() => {
    if (!enabled) return;
    if (!form.patient_id || !form.professional_id) return;
    if (skipWorkflowAutosaveRef.current) {
      skipWorkflowAutosaveRef.current = false;
      return;
    }
    void saveDraft();
  }, [
    enabled,
    form.patient_id,
    form.professional_id,
    attachedPrintables,
    indicationPrintable,
    procedureLabels,
    outcome,
    saveDraft,
  ]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const lastSaveFailed = useCallback(() => lastSaveFailedRef.current, []);

  return { status, lastSavedAt, lastError, lastSaveFailed, flushAutosave, saveDraft, saveDraftWithOverrides };
}

export function evolutionDraftStatusLabel(
  status: EvolutionDraftSaveStatus,
  lastSavedAt: Date | null,
  concluded = false,
): string {
  const noun = concluded ? 'Alterações' : 'Rascunho';
  switch (status) {
    case 'pending':
      return 'Alterações pendentes…';
    case 'saving':
      return concluded ? 'Salvando alterações…' : 'Salvando rascunho…';
    case 'saved':
      return lastSavedAt
        ? `${noun} salv${concluded ? 'as' : 'o'} às ${lastSavedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
        : `${noun} salv${concluded ? 'as' : 'o'}`;
    case 'error':
      return concluded ? 'Falha ao salvar alterações' : 'Falha ao salvar rascunho';
    default:
      return '';
  }
}
